<template>
  <div class="l-page">
    <Loading v-show="isLoading" variant="page" />
    <template v-if="!isLoading || isProxyRoute">
      <div class="l-content">
        <Side />
        <main class="l-main" :class="{ 'is-hidden': !roomId }">
          <Header />
          <Loading v-show="isLoadingMain" variant="main" />
          <router-view v-if="!isLoadingMain" />
        </main>
      </div>
      <Footer />
    </template>
  </div>
</template>

<script>
import Footer from '@/components/layouts/Footer';
import Header from '@/components/layouts/Header';
import Loading from '@/components/Loading';
import Side from '@/components/layouts/Side';
import store from '@/store/store';
import utils from '@/utils';
import axios from 'axios';
import {
  ANALYTICS_USER_TYPE,
  EXTERNAL_SERVICE,
  EXTERNAL_SERVICE_ACTION
} from '@/analytics/constants';
import {
  reportExternalServiceError,
  setExternalServiceErrorIdentity
} from '@/analytics/reportExternalServiceError';

export default {
  components: {
    Footer,
    Header,
    Loading,
    Side
  },
  computed: {
    role() {
      return this.$store.state.role;
    },
    roomId() {
      return this.$store.getters.currentRoomId;
    },
    user() {
      return this.$store.state.user;
    },
    isLoading() {
      return this.$store.state.isLoading;
    },
    isLoadingMain() {
      return this.$store.state.isLoadingMain;
    },
    isAuthenticated() {
      return this.$store.state.isAuthenticated;
    },
    isLogined() {
      return this.$store.state.isLogined;
    },
    isProxyRoute() {
      // ProxyBootstrapはisLoadingを自ら解除する側なので、isLoadingの陰に隠れたままだと
      // 一生マウントされず、代理ログインのブートストラップが進まなくなる
      return this.$route.name === 'proxy';
    },
    isReadOnlyMode() {
      return this.$store.state.isReadOnlyMode;
    }
  },
  watch: {
    isAuthenticated: {
      handler(value) {
        // 代理ログイン(閲覧のみ)モードではProxyBootstrapが既にプロジェクトを取得済みのため、
        // ここで取得すると代理店/インフルエンサー本人のプロジェクトで上書きしてしまう
        if (value && !this.$store.state.isReadOnlyMode) {
          this.getProjects();
        }
      },
      immediate: true
    }
  },
  mounted() {
    // ProxyBootstrapが取得したプロジェクト一覧からルーム一覧を組み立てる
    this.$root.$on('proxy-projects-loaded', this.setProjectsAndRooms);
  },
  beforeDestroy() {
    this.$root.$off('proxy-projects-loaded', this.setProjectsAndRooms);
  },
  methods: {
    waitForLogin() {
      // Firestoreを読むにはFirebaseのログインが必要
      if (this.isLogined) {
        return Promise.resolve();
      }
      return new Promise(resolve => {
        const unwatch = this.$watch('isLogined', value => {
          if (value) {
            unwatch();
            resolve();
          }
        });
      });
    },
    getProjects() {
      // プロジェクト一覧はAuth0のidTokenだけで取得できるため、Firebaseのログインと並行して取得する
      const authId = this.user.sub.replace(/^auth0\|/, '');
      return axios
        .get(`${process.env.VUE_APP_COMPANY_SITE_URL}/projects`, {
          headers: {
            Authorization: 'Bearer ' + store.state.user.idToken,
            'x-api-key': process.env.VUE_APP_X_API_KEY
          },
          params: {
            auth_id: authId,
            r: this.role.query
          }
        })
        .then(response => {
          const projects = response.data.projects || [];
          return this.waitForLogin().then(() =>
            this.setProjectsAndRooms(projects, response.data.userId)
          );
        })
        .catch(() => {
          this.$store.dispatch('setLoading', false);
        });
    },
    setProjectsAndRooms(projects, userId) {
      const userType = utils.isCompany(this.role.name)
        ? ANALYTICS_USER_TYPE.BUSINESS
        : ANALYTICS_USER_TYPE.INFLUENCER;
      setExternalServiceErrorIdentity(userId, userType);

      const rooms = this.getRooms(projects);
      this.$store.dispatch('setProjects', projects);
      this.$store.dispatch('setRooms', rooms);

      // Firestore上のルームのデータは読み込みを待たずに、届き次第反映する
      rooms.forEach(room => {
        this.setRoomListener(room);
      });
      this.$store.dispatch('setLoading', false);
    },
    getRooms(projects) {
      if (utils.isCompany(this.role.name)) {
        return this.getCompanyRooms(projects);
      } else if (utils.isInfluencer(this.role.name)) {
        return this.getInfluencerRooms(projects);
      }
    },
    getCompanyRooms(projects) {
      let rooms = [];
      if (projects.length) {
        projects.forEach(project => {
          project.partners.forEach(partner => {
            const roomId = utils.getRoomId({
              partnerId: partner.id,
              projectId: project.id,
              roleName: this.role.name,
              userId: this.$store.getters.effectiveSub
            });
            const room = {
              countUnread: 0,
              companyManualUnreadDatetime: null,
              companyManualUnreadMessageId: null,
              id: roomId,
              isSynced: false,
              lastDatetime: null,
              partnerAvatar: partner.avatar,
              partnerLastDatetime: null,
              partnerName: partner.name,
              partnerId: partner.id,
              influencerId: partner.influencer_id,
              projectName: project.name,
              projectId: project.id,
              renewalProjectId: project.project_id
            };
            rooms.push(room);
          });
        });
      }
      return rooms;
    },
    getInfluencerRooms(projects) {
      let rooms = [];
      if (projects.length) {
        projects.forEach(project => {
          const roomId = utils.getRoomId({
            partnerId: project.partner.id,
            projectId: project.id,
            roleName: this.role.name,
            userId: this.$store.getters.effectiveSub
          });
          const room = {
            countUnread: 0,
            companyManualUnreadDatetime: null,
            companyManualUnreadMessageId: null,
            id: roomId,
            isSynced: false,
            lastDatetime: null,
            partnerAvatar: project.partner.avatar,
            partnerLastDatetime: null,
            partnerName: project.partner.name,
            partnerId: project.partner.id,
            influencerId: project.partner.influencer_id,
            projectName: project.name,
            projectId: project.id,
            renewalProjectId: project.project_id
          };
          rooms.push(room);
        });
      }
      return rooms;
    },
    setRoomListener(room) {
      const ref = this.$firebase.db.collection('rooms').doc(room.id);
      // 最初のスナップショットをget()の代わりに使い、ルームごとの往復を1回にする
      let isSynced = false;
      const setSynced = () => {
        if (!isSynced) {
          isSynced = true;
          this.$store.dispatch('setRoom', { id: room.id, isSynced: true });
        }
      };
      let listener = ref.onSnapshot(
        doc => {
          if (!isSynced && !doc.exists && doc.metadata.fromCache) {
            // オフライン時はサーバーの結果ではなく空のキャッシュが届くので、ルームを作らずにサーバーの結果を待つ
            // ここでルームを作ると、オンラインに戻った時に既存のルームの最終閲覧日時と未読数を上書きしてしまう
            return;
          }
          if (!isSynced && !doc.exists) {
            // Firestoreにルームが存在しなかったら作る
            this.createFirestoreRoom(room);
          } else {
            // 最終閲覧日時に変更があったらStoreに保存
            this.setRoomLastDatetime(room, doc.data());
          }
          setSynced();
        },
        error => {
          reportExternalServiceError({
            service: EXTERNAL_SERVICE.FIRESTORE,
            action_type: isSynced
              ? EXTERNAL_SERVICE_ACTION.CHAT_ROOM_LISTEN
              : EXTERNAL_SERVICE_ACTION.CHAT_ROOM_READ,
            error
          });
          setSynced();
        }
      );
      this.$firebase.setListener(listener);
    },
    resetRoomListeners() {
      this.$firebase.resetListeners();
    },
    setRoomLastDatetime(room, data) {
      if (!data) {
        // 代理店権限でルームが作成されていない状態だとここを通る
        return;
      }
      this.$store.dispatch('setRoom', {
        company_uid: data.company_uid,
        companyManualUnreadDatetime:
          data.company_manual_unread_datetime || null,
        companyManualUnreadMessageId:
          data.company_manual_unread_message_id || null,
        id: room.id,
        influencer_uid: data.influencer_uid,
        lastDatetime: data[this.role.name + '_last_datetime'],
        partnerLastDatetime: data[this.role.partnerName + '_last_datetime']
      });
    },
    createFirestoreRoom(room) {
      // 閲覧のみモードではルームを作成しない。setRoomListener()から自動で呼ばれるため
      // ユーザー操作の起点がなく、firestore.rulesでも防げない
      if (this.isReadOnlyMode) {
        return;
      }
      // roomを作れるのは代理店のみ
      if (!utils.isCompany(this.role.name)) {
        return;
      }
      // ルームのフィールドに保存するタイムスタンプが、これから送信するメッセージのタイムスタンプよりも確実に過去になるように0にしておく
      const datetime = this.$firebase.getTimestamp(0, 0);

      this.$store.dispatch('setRoom', {
        companyManualUnreadDatetime: null,
        companyManualUnreadMessageId: null,
        id: room.id,
        lastDatetime: datetime,
        partnerLastDatetime: datetime
      });
      this.$firebase.createRoom({
        companyId: this.user.sub,
        datetime: datetime,
        id: room.id,
        influencerId: room.partnerId,
        projectId: room.projectId
      });
    }
  }
};
</script>
